import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { LovDataService } from '../lov-data/lov-data.service';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { LeadsService } from 'src/app/business-service/leads/leads.service';
import { Location } from '@angular/common';
import { RECORD_STATUS_VALUES, TRAVEL_SERVICES } from '../../constants/api-constants';
import { jwtDecode } from 'jwt-decode';
import { ClientAdminService } from 'src/app/business-service/client-admin/client-admin.service';
import { ResponseNotificationService } from '../response-notification/response-notification.service';
import { UserRolesService } from 'src/app/business-service/user-roles/user-roles.service';
import { AdminService } from 'src/app/business-service/admin/admin.service';

@Injectable({
  providedIn: 'root'
})
export class GlobalService {
  private _roles = new BehaviorSubject<any>(null);
  roles$ = this._roles.asObservable();
  rolesAndPermissions: any;


  // Show or hide spinner observalble
  private showSpinner = new BehaviorSubject<boolean>(false);
  showSpinner$ = this.showSpinner.asObservable();

  private activeAPIRequests = 0;

  userSenderAccess: any;
  organizationEmailSenderAccess: any;
  communicationEmailSenderAccess: any;

  recommendedItinerary: any;

  //////////////////////////////
  // TODO: Remove these variables later after TG demo
  /////////////////////////////
  userTripDetails: any;
  userPreferences: any;
  usersLocations: any;
  aiRouteCraft: any;

  setUserTripDetails(details: any): void {
    this.userTripDetails = details;
    localStorage.setItem('userTripDetails', JSON.stringify(details));
  }
  getUserTripDetails(): any {
    if (!this.userTripDetails) {
      const stored = localStorage.getItem('userTripDetails');
      this.userTripDetails = stored ? JSON.parse(stored) : null;
    }
    return this.userTripDetails;
  }
  setUserPreferences(preferences: any): void {
    this.userPreferences = preferences;
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
  }
  getUserPreferences(): any {
    if (!this.userPreferences) {
      const stored = localStorage.getItem('userPreferences');
      this.userPreferences = stored ? JSON.parse(stored) : null;
    }
    return this.userPreferences;
  }
  setUsersLocations(locations: any): void {
    this.usersLocations = locations;
    localStorage.setItem('usersLocations', JSON.stringify(locations));
  }
  getUsersLocations(): any {
    if (!this.usersLocations) {
      const stored = localStorage.getItem('usersLocations');
      this.usersLocations = stored ? JSON.parse(stored) : null;
    }
    return this.usersLocations;
  }
  setAiRouteCraft(data: any): void {
    this.aiRouteCraft = data;
    localStorage.setItem('aiRouteCraft', JSON.stringify(data));
  }
  getAiRouteCraft(): any {
    if (!this.aiRouteCraft) {
      const stored = localStorage.getItem('aiRouteCraft');
      this.aiRouteCraft = stored ? JSON.parse(stored) : null;
    }
    return this.aiRouteCraft;
  }

  //////////////////////////////
  // TODO: Remove these variables later after TG demo
  /////////////////////////////

  constructor(
    private _leadService: LeadsService,
    private _alertController: AlertController,
    private _location: Location,
    private _userService: ClientAdminService,
    private _responseNotificationService: ResponseNotificationService,
    private _userRoles: UserRolesService,
    private _adminService: AdminService
  ) { }

  setRecommendedItinerary(itinerary: any): void {
    this.recommendedItinerary = itinerary;
  }
  getRecommendedItinerary(): any {
    return this.recommendedItinerary;
  }

  // Method to set roles data
  setRoles(roles: any): void {
    this.rolesAndPermissions = roles;
    this._roles.next(roles);
  }

  // Method to get roles data
  getPermission(moduleName: string, target: string): boolean | undefined {
    if (this.rolesAndPermissions) {
      const module = this.rolesAndPermissions.modules.find((module: any) => module.name === moduleName);
      if (module) {
        const permission = module.permissions[target];
        if (permission !== undefined) {
          return permission;
        } else {
          console.error(`Permission ${target} not found in module ${moduleName}`);
          return undefined;
        }
      } else {
        console.error(`Module ${moduleName} not found`);
        return undefined;
      }
    } else {
      console.error("Roles and permissions not set yet.");
      return undefined;
    }

  }

  setUserSenderAccess(value: any) {
    this.userSenderAccess = value;
  }
  getUserSenderAccess() {
    return this.userSenderAccess;
  }

  setOrganizationEmailSenderAccess(value: any) {
    this.organizationEmailSenderAccess = value;
  }

  getOrganizationEmailSenderAccess() {
    return this.organizationEmailSenderAccess?.senderAccess;
  }
  setOrganizationCommunicationEmailSenderAccess(value: any) {
    this.communicationEmailSenderAccess = value;
  }

  getOrganizationCommunicationEmailSenderAccess(email: string) {
    if (this.communicationEmailSenderAccess && this.communicationEmailSenderAccess.length > 0) {
      let targetEmail = this.communicationEmailSenderAccess.filter((a: any) => a.email === email);
      if (targetEmail.length > 0) {
        return targetEmail[0].senderAccess;
      }
    }
  }

  retrieveOrganizationVerifiedEmails(): string[] {
    let allFromEmail: string[] = [];

    if (this.organizationEmailSenderAccess && this.organizationEmailSenderAccess?.senderAccess === "SUCCESS") {
      allFromEmail.push(this.organizationEmailSenderAccess.email);
    }

    if (this.communicationEmailSenderAccess) {
      this.communicationEmailSenderAccess.forEach((a: any) => {
        if (a.senderAccess === "SUCCESS") {
          allFromEmail.push(a.email);
        }
      });
    }

    return allFromEmail;
  }

  // Method to get current roles data
  getRoles(): any {
    return this._roles.value;
  }

  /////////////////////////
  // Spinner Funcationality
  /////////////////////////

  // Show the spinner
  showLoadingSpinner() {
    this.activeAPIRequests++;
    this.updateSpinnerVisibility();
  }

  // Hide the spinner
  hideLoadingSpinner() {
    if (this.activeAPIRequests > 0) {
      this.activeAPIRequests--;
    }
    this.updateSpinnerVisibility();
  }

  // Control spinner visibility based on active requests
  private updateSpinnerVisibility() {
    if (this.activeAPIRequests > 0) {
      // Show spinner
      this.showSpinner.next(true)
    } else {
      // Hide spinner
      this.showSpinner.next(false)
    }
  }

  /////////////////////////
  // Alert / Confirm popups
  /////////////////////////


  public async confirmActionPopup(message: string, heading: string = 'Confirm', trueBtn: string = 'OK'): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const alert = await this._alertController.create({
        header: heading,
        message: message,
        cssClass: 'confirm-message-main',
        buttons: [
          {
            text: 'x',
            role: 'cancel',
            cssClass: 'false-btn',
            handler: () => resolve(false),
          },
          {
            text: trueBtn,
            role: 'confirm',
            cssClass: 'true-btn',
            handler: () => resolve(true),
          },
        ],
      });

      await alert.present();
    });
  }

  // Function with 3 actions (save & proceed, proceed and cancel)
  public async confirmTabChangeAction(message: string, heading: string = 'Confirm', saveProceedBtn: string = 'Save and Proceed', proceedBtn: string = 'Proceed without Saving'): Promise<string> {
    return new Promise<string>(async (resolve) => {
      const alert = await this._alertController.create({
        header: heading,
        message: message,
        cssClass: 'confirm-message-main large',
        buttons: [
          {
            text: 'x',
            role: 'cancel',
            cssClass: 'false-btn',
            handler: () => resolve('close'),
          },
          {
            text: proceedBtn,
            role: 'proceed',
            cssClass: 'proceed-btn',
            handler: () => resolve('proceed'),
          },
          {
            text: saveProceedBtn,
            role: 'saveProceed',
            cssClass: 'save-proceed-btn',
            handler: () => resolve('saveProceed'),
          },
        ],
      });

      await alert.present();
    });
  }


  // Function with 3 actions (save & proceed, proceed and cancel)
  public async confirmInvoiceDownloadAction(message: string, heading: string = 'Confirm', yesButtonText: string = 'Yes', noButtonText: string = 'No'): Promise<any> {
    return new Promise<any>(async (resolve) => {
      const alert = await this._alertController.create({
        header: heading,
        message: message,
        cssClass: 'confirm-message-main large',
        buttons: [
          {
            text: 'x',
            role: 'cancel',
            cssClass: 'false-btn',
            handler: () => resolve(false),
          },
          {
            text: yesButtonText,
            role: 'proceed',
            cssClass: 'proceed-btn',
            handler: () => resolve('Yes'),
          },
          {
            text: noButtonText,
            role: 'saveProceed',
            cssClass: 'save-proceed-btn',
            handler: () => resolve('No'),
          },
        ],
      });

      await alert.present();
    });
  }


  //POP up alert message
  public async customAlert(message: string, header: string = 'Alert') {
    const alert = await this._alertController.create({
      header: header,
      message: message,
      buttons: [
        {
          text: 'x',
          cssClass: 'false-btn',
        }
      ],
      cssClass: 'alert-message-main'
    });
    await alert.present();
  }


  ///////////////////
  // global functions
  ///////////////////

  navigatePrev() {
    this._location.back();
  }

  navigateNext() {
    this._location.forward();
  }

  sortDataInDescendingOrder(data: any[], key: string): void {
    data.sort((a: any, b: any) => {
      if (a[key] > b[key]) {
        return -1;
      } else if (a[key] < b[key]) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  getTodaysDate() {
    const today = new Date();
    today.setMinutes(today.getMinutes() + today.getTimezoneOffset()); // Handle timezone offset
    return today.toISOString().split('T')[0];
  }


  getTodaysDateTime() {
    const today = new Date();
    today.setMinutes(today.getMinutes() + today.getTimezoneOffset()); // Handle timezone offset
    return today.toISOString().split('T')[0] + 'T' + new Date().toLocaleTimeString('it-IT').slice(0, 5);
  }

  getLeadsTravelServices(leadId: any): Promise<any> {
    return new Promise((resolve, reject) => {
      let serviceBasedSections = {
        showHotels: true,
        showRouteCraft: true,
        showTransportation: true,
        showTransfers: true,
        showTourPackage: true
      };

      this._leadService.getLead(leadId).subscribe(
        (data) => {
          let leadTravelServices = data.travelServices;

          // Update tabs based on leads travel services
          serviceBasedSections.showHotels = leadTravelServices.includes(TRAVEL_SERVICES.HOTELS) ? true : false;
          serviceBasedSections.showRouteCraft = leadTravelServices.includes(TRAVEL_SERVICES.SIGHTSEEING) ? true : false;
          serviceBasedSections.showTransportation = leadTravelServices.includes(TRAVEL_SERVICES.TRANSPORTATION) ? true : false;
          serviceBasedSections.showTransfers = leadTravelServices.includes(TRAVEL_SERVICES.TRANSFERS) ? true : false;
          serviceBasedSections.showTourPackage = leadTravelServices.includes(TRAVEL_SERVICES.TOUR_PACKAGE) ? true : false;

          resolve(serviceBasedSections);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  // Utility function to calculate age from date of birth
  calculateAge(dob: string): string {
    if (!dob) {
      return ""
    }

    const birthDate = new Date(dob);
    const today = new Date();

    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();

    // Adjust years and months if the birthday hasn't occurred this year yet
    if (months < 0) {
      years--;
      months += 12;
    }

    const days = today.getDate() - birthDate.getDate();
    if (days < 0) {
      months--;
      if (months < 0) {
        years--;
        months += 11;
      }
    }

    if (years > 0 && months > 0) {
      return `${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}`;
    } else if (years > 0) {
      return `${years} year${years > 1 ? 's' : ''}`;
    } else if (months > 0) {
      return `${months} month${months > 1 ? 's' : ''}`;
    } else {
      return `Less than a month`;
    }
  }


  // format datetime into date

  formatDateTimeToDate(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2); // Months are zero-based
    const day = ('0' + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  // convert number into words

  units: string[] = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  teens: string[] = ['Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  tens: string[] = ['', 'Ten', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  thousands: string[] = ['Thousand', 'Lakh', 'Crore'];

  convertNumberToWords(num: number): string {
    num = Math.floor(num);
    if (num === 0) {
      return 'Zero';
    }

    let words = '';

    if (Math.floor(num / 10000000) > 0) {
      words += this.convertNumberToWords(Math.floor(num / 10000000)) + ' Crore ';
      num %= 10000000;
    }

    if (Math.floor(num / 100000) > 0) {
      words += this.convertNumberToWords(Math.floor(num / 100000)) + ' Lakh ';
      num %= 100000;
    }

    if (Math.floor(num / 1000) > 0) {
      words += this.convertNumberToWords(Math.floor(num / 1000)) + ' Thousand ';
      num %= 1000;
    }

    if (Math.floor(num / 100) > 0) {
      words += this.convertNumberToWords(Math.floor(num / 100)) + ' Hundred ';
      num %= 100;
    }

    if (num > 0) {
      if (words !== '') {
        words += 'and ';
      }

      if (num < 10) {
        words += this.units[num];
      } else if (num > 10 && num < 20) {
        words += this.teens[num - 11];
      } else {
        words += this.tens[Math.floor(num / 10)];
        if ((num % 10) > 0) {
          words += '-' + this.units[num % 10];
        }
      }
    }

    return words.trim();
  }

  getFirstItemOfString(input: string): string {
    const items = input.split(',');
    return items[0].trim();
  }

  // scroll to first invalid fields

  scrollToFirstInvalidControl(): void {
    const firstInvalidControl: HTMLElement = document.querySelector(
      'form .ng-invalid'
    ) as HTMLElement;

    if (firstInvalidControl) {
      firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInvalidControl.focus();
    }
  }

  scrollToFirstInvalidControlInContainer(containerSelector: string): void {
    const scrollContainer = document.querySelector(`${containerSelector}`) as HTMLElement;

    if (!scrollContainer) {
      console.warn(`Container with class '${containerSelector}' not found.`);
      return;
    }

    const invalidControl = scrollContainer.querySelector('.ng-invalid') as HTMLElement;

    if (invalidControl) {
      scrollContainer.scrollTo({
        top: invalidControl.offsetTop - scrollContainer.offsetTop,
        behavior: 'smooth'
      });

      invalidControl.focus({ preventScroll: true });
    }
  }

  scrollToBottomOfContainer(containerSelector: string): void {
    const container = document.querySelector(containerSelector);
    if (container) {
      container.scrollTop = container.scrollHeight;
    } else {
      console.warn(`Container not found for selector: ${containerSelector}`);
    }
  }


  // Convert seconds to hh:mm:ss format
  secondsToTime(seconds: number): string {
    if (isNaN(seconds) || seconds < 0) {
      return 'Invalid time';
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const pad = (num: number) => (num < 10 ? '0' + num : num);

    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
  }

  // convert data to strings
  convertDataToStrings(tempVariable: any): any {

    const convertedVariable: any = {};

    for (const key in tempVariable) {
      if (Array.isArray(tempVariable[key])) {
        // Check the type of the array elements and process accordingly
        convertedVariable[key] = tempVariable[key].map((item: any) => {
          return typeof item === 'object' && item !== null ? item.data : item;
        });
      }
    }
    return convertedVariable;
  }

  // Fuction to combine date and time
  combineDateAndTime(date: Date | null, time: Date | null): Date | null {
    if (!date && !time) {
      return null;
    }
    // If only date is provided, use its date and default time to 00:00:00.000
    if (date && !time) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    }
    // If only time is provided, use today's date and time from the time parameter
    if (!date && time) {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth(), today.getDate(), time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds());
    }
    // If both are provided, combine them
    if (date && time) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds());
    }
    return null;
  }

  convertDateToString(date: Date | null): string {
    if (!date) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }


  convertTripDateToString(date: Date | null): string {
    if (!date) {
      return '';
    }
    let newDate = date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    let dateParts = newDate.split('/');
    let formattedDate = `${dateParts[2]}-${dateParts[0]}-${dateParts[1]}T00:00:00`; // yyyy-mm-ddT00:00:00 format
    return formattedDate;
  }


  // Filtering lead status based on existing status value

  filterLeadStatusLov(statusValue: string, leadStatusLov: any[]): any[] {
    if (leadStatusLov) {
      // Removing statuses which are following workflow
      leadStatusLov = leadStatusLov.map((item: any) =>
        item.key === RECORD_STATUS_VALUES.IN_PROGRESS ||
          item.key === RECORD_STATUS_VALUES.QUOTE_CREATED ||
          item.key === RECORD_STATUS_VALUES.CONVERTED
          ? { ...item, disabled: true }
          : item
      );

      switch (statusValue) {
        case RECORD_STATUS_VALUES.IN_PROGRESS: {
          leadStatusLov = leadStatusLov.map((item: any) =>
            item.key === RECORD_STATUS_VALUES.NEW ||
              item.key === RECORD_STATUS_VALUES.CONTACTED ||
              item.key === RECORD_STATUS_VALUES.QUOTATION_PENDING
              ? { ...item, disabled: true }
              : item
          );
          break;
        }
        case RECORD_STATUS_VALUES.QUOTE_CREATED: {
          leadStatusLov = leadStatusLov.map((item: any) =>
            item.key === RECORD_STATUS_VALUES.NEW ||
              item.key === RECORD_STATUS_VALUES.CONTACTED ||
              item.key === RECORD_STATUS_VALUES.QUOTATION_PENDING ||
              item.key === RECORD_STATUS_VALUES.JUNK
              ? { ...item, disabled: true }
              : item
          );
          break;
        }
        case RECORD_STATUS_VALUES.CONVERTED: {
          leadStatusLov = leadStatusLov.map((item: any) =>
            item.key === RECORD_STATUS_VALUES.NEW ||
              item.key === RECORD_STATUS_VALUES.CONTACTED ||
              item.key === RECORD_STATUS_VALUES.QUOTATION_PENDING ||
              item.key === RECORD_STATUS_VALUES.JUNK ||
              item.key === RECORD_STATUS_VALUES.LOST
              ? { ...item, disabled: true }
              : item
          );
          break;
        }
      }

      // Enable the specific statusValue item
      leadStatusLov = leadStatusLov.map((item: any) =>
        item.key === statusValue ? { ...item, disabled: false } : item
      );

      return leadStatusLov;
    } else {
      return [];
    }
  }

  formatPhoneNumber(data: any[], key: string): any[] {
    if (!Array.isArray(data)) {
      console.error('Expected an array, received:', data);
      return [];
    }

    data.forEach((element: any) => {
      if (typeof element[key] === 'object' && element[key] !== null) {
        const phoneNumber = element[key].phone || '';
        const countryCode = element[key].countryCode || '';
        element[key] = `${countryCode} ${phoneNumber.replace(/\D/g, '')}`.trim();
      } else if (typeof element[key] === 'string') {
        const defaultCountryCode = '+91';
        const phoneNumber = element[key] || '';
        element[key] = `${defaultCountryCode} ${phoneNumber.replace(/\D/g, '')}`.trim();
      }
    });

    return data;
  }


  formatDestination(
    data: Array<{ destination?: { location: string }[] }>,
    key: 'destination'
  ): string[] {
    if (!data || !Array.isArray(data) || !key) {
      return [];
    }

    return data.map((item) => {
      if (!item || !item[key]) {
        console.warn(`Invalid or missing destination for item: ${JSON.stringify(item)}`);
        return '';
      }

      const destinations = item[key];

      if (!destinations || destinations.length === 0) {
        return '';
      }

      // Format the destination locations as a comma-separated string
      return destinations.map((dest) => dest.location).join(', ');
    });
  }
  // It is updated to format date in DD/MM/YYYY format as per version9 requirement
  formatDate(dateString: string, format: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {};

    if (format === 'DD/MM/YYYY') {
      options.day = '2-digit';
      options.month = '2-digit';
      options.year = 'numeric';
    };

    return date.toLocaleDateString('en-GB', options); // -> 05/11/2025
  }


  convertToCSV(data: any[], columnConfig: { key: string; title: string }[], fileName: string): void {
    // Check if data is empty, null or undefined
    if (!data || data.length === 0) {
      // Generate the CSV with only the headers
      const headers = columnConfig.map((col) => `"${col.title}"`).join(',');
      const csvData = `${headers}\n`;  // Just headers, no rows

      // Proceed to download the empty CSV file
      this.downloadCSV(csvData, fileName);
      return;
    }

    // If data is not empty, generate CSV as usual
    const headers = columnConfig.map((col) => `"${col.title}"`).join(',');

    const rows = data.map((row) => {
      return columnConfig.map((col) => {
        const value = row[col.key];
        const stringValue =
          value === null || value === undefined ? '' : value.toString();
        return `"${stringValue.replace(/"/g, '""')}"`;
      }).join(',');
    });

    const csvData = `${headers}\n${rows.join('\n')}`;
    this.downloadCSV(csvData, fileName);
  }

  // Method to trigger the actual download of the CSV file
  private downloadCSV(csvData: string, fileName: string): void {
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `${fileName}.csv`);
    a.click();
    window.URL.revokeObjectURL(url);
  }

  downloadAndSaveFile(url: string, fileName: string): void {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const objectURL = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('href', objectURL);
        a.setAttribute('download', fileName);
        a.click();

        URL.revokeObjectURL(objectURL);
      })
      .catch(error => console.error('Error downloading file:', error));
  }

  // Function to trim string based on length and add ellipsis
  trimString(str: string, maxLength: number): string {
    if (!str || typeof str !== 'string') {
      return '';
    }
    if (str.length > maxLength) {
      return `${str.substring(0, maxLength)}...`;
    }
    return str;
  }

  retrieveEmailsFromToken(includeOrganizationEmail: boolean = false) {
    const token = localStorage.getItem("Authorization");
    if (!token) {
      console.error("No token found in localStorage.");
      return;
    }
    const decodedToken: any = jwtDecode(token);
    // Extracting CEmail and CCEmails from the token
    const cEmail = decodedToken["CEmail"];
    const ccEmails = decodedToken["CCEmails"] ? decodedToken["CCEmails"].split(",") : [];
    // Combine both into a single array
    const allFromEmails = includeOrganizationEmail ? [cEmail, ...ccEmails] : [...ccEmails];
    return allFromEmails;
  }

  getuseraccessdetails() {
    let userToken: any = jwtDecode(localStorage.getItem('Authorization') ?? "");
    if (userToken.SenderAccess === "SUCCESS")
      return false;
    else
      return true
  }


  senderAccess(email: string) {
    this._userService.requestSenderEmailAccess(email).subscribe({
      next: (data: any) => {
        this._responseNotificationService.showMessage("We have sent a verification link. Please click on 'Verify' to gain sender access.");
        let role = localStorage.getItem("Role");
        this._userRoles.getPermissions(role).subscribe(
          (data) => {
            this.setUserSenderAccess(data.userSenderAccess);
            this.setOrganizationEmailSenderAccess(data.organizationSenderAccess);
            this.setOrganizationCommunicationEmailSenderAccess(data.organizationCommunicationEmails);
          },
          (error) => {
            console.error("Failed to fetch permissions", error);
          }
        );
      }
    });
  }


  async getLoggedInOrgDetails(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this._adminService.getOrganization(localStorage.getItem("CId")).subscribe({
        next: (data) => {
          resolve(data);
        }
      })
    })
  }


}
